/**
 * Evento global disparado após um gasto de créditos de IA bem-sucedido.
 * Os indicadores de créditos (topbar, navbar do chat-ia) escutam para
 * atualizar o saldo na hora com uma animação de "-N" — sem esperar refetch.
 */
export const IA_CREDITS_SPENT_EVENT = "gamefik:ia-credits-spent"

export type IaCreditsSpentDetail = { cost: number; remaining: number }
export type IaCreditsCommittedDetail = IaCreditsSpentDetail & { operationId: string }

export type IaCreditsReservationStatus = "reserved" | "committed" | "released" | "expired"

export type IaCreditsReservationSnapshot = {
  operationId: string
  status: IaCreditsReservationStatus
  cost: number
  remaining: number
  total: number
  isUnlimited: boolean
}

export type IaCreditsReservationLookup =
  | { kind: "snapshot"; snapshot: IaCreditsReservationSnapshot }
  | { kind: "not_found" }
  | { kind: "unauthenticated" }
  | { kind: "forbidden" }
  | { kind: "invalid_request" }
  | { kind: "unavailable" }

export type IaCreditsRecoveryPolicy = {
  action:
    | "poll"
    | "stop"
    | "auth_error"
    | "invalid_request"
    | "committed_without_result"
    | "use_final_result"
  retry: "blocked" | "same_operation" | "new_operation"
}

export type IaCreditsGenerationError =
  | {
      code: "NO_IA_CREDITS"
      needed: number
      remaining: number
      total?: number
    }
  | {
      code:
        | "AUTHENTICATION_REQUIRED"
        | "AUTHORIZATION_FORBIDDEN"
        | "OPERATION_IN_PROGRESS"
        | "PAYLOAD_TOO_LARGE"
        | "SERVICE_UNAVAILABLE"
        | "GENERATION_FAILED"
    }

export function buildIaCreditsProtocolHeaders(
  authorization: string,
  operationId: string
): Record<string, string> {
  return {
    "Content-Type": "application/json",
    Authorization: authorization,
    "X-IA-Credits-Protocol": "reservation-v2",
    "X-Idempotency-Key": operationId,
  }
}

export function notifyIaCreditsSpent(detail: IaCreditsSpentDetail): void {
  if (typeof window === "undefined") return
  window.dispatchEvent(new CustomEvent<IaCreditsSpentDetail>(IA_CREDITS_SPENT_EVENT, { detail }))
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function asNonNegativeFiniteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? value
    : null
}

/**
 * Converte somente o commit confirmado pelo stream v2 no detalhe consumido
 * pelos indicadores de saldo. Reserva, release, expiração e payload inválido
 * não representam gasto confirmado e, por isso, não disparam animação.
 */
export function parseIaCreditsStreamEvent(event: unknown): IaCreditsCommittedDetail | null {
  const root = asRecord(event)
  const data = asRecord(root?.data)
  if (root?.type !== "credits" || data?.status !== "committed") return null

  const operationId = data.operationId
  const cost = asNonNegativeFiniteNumber(data.cost)
  const remaining = asNonNegativeFiniteNumber(data.remaining)
  if (
    typeof operationId !== "string"
    || !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(operationId)
    || cost === null
    || remaining === null
  ) {
    return null
  }

  return { operationId, cost, remaining }
}

const IA_CREDITS_RESERVATION_STATUSES = new Set<IaCreditsReservationStatus>([
  "reserved",
  "committed",
  "released",
  "expired",
])

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID_PATTERN.test(value)
}

export function parseIaCreditsReservationStatusResponse(
  httpStatus: number,
  payload: unknown,
  expectedOperationId: string
): IaCreditsReservationLookup {
  const root = asRecord(payload)
  if (httpStatus === 404 && root?.error === "OPERATION_NOT_FOUND") {
    return { kind: "not_found" }
  }
  if (httpStatus === 400) return { kind: "invalid_request" }
  if (httpStatus === 401) return { kind: "unauthenticated" }
  if (httpStatus === 403) return { kind: "forbidden" }
  if (httpStatus !== 200 || !root) return { kind: "unavailable" }

  const status = root.status
  const cost = asNonNegativeFiniteNumber(root.cost)
  const remaining = asNonNegativeFiniteNumber(root.remaining)
  const total = asNonNegativeFiniteNumber(root.total)
  if (
    !isUuid(root.operationId)
    || root.operationId !== expectedOperationId
    || typeof status !== "string"
    || !IA_CREDITS_RESERVATION_STATUSES.has(status as IaCreditsReservationStatus)
    || cost === null
    || remaining === null
    || total === null
    || typeof root.isUnlimited !== "boolean"
  ) {
    return { kind: "unavailable" }
  }

  return {
    kind: "snapshot",
    snapshot: {
      operationId: root.operationId,
      status: status as IaCreditsReservationStatus,
      cost,
      remaining,
      total,
      isUnlimited: root.isUnlimited,
    },
  }
}

export async function requestIaCreditsReservationStatus({
  apiBaseUrl,
  authorization,
  operationId,
  signal,
  fetchImpl = fetch,
}: {
  apiBaseUrl: string
  authorization: string
  operationId: string
  signal?: AbortSignal
  fetchImpl?: typeof fetch
}): Promise<IaCreditsReservationLookup> {
  if (!isUuid(operationId)) return { kind: "unavailable" }

  try {
    const response = await fetchImpl(
      `${apiBaseUrl.replace(/\/+$/, "")}/ia-credits/reservations/${encodeURIComponent(operationId)}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: authorization,
        },
        signal,
      }
    )
    const payload = await response.json().catch(() => null)
    return parseIaCreditsReservationStatusResponse(response.status, payload, operationId)
  } catch (error) {
    if (signal?.aborted || (error instanceof Error && error.name === "AbortError")) throw error
    return { kind: "unavailable" }
  }
}

export function getIaCreditsRecoveryPolicy(
  lookup:
    | { kind: "not_found" }
    | { kind: "unauthenticated" }
    | { kind: "forbidden" }
    | { kind: "invalid_request" }
    | { kind: "unavailable" }
    | {
        kind: "snapshot"
        snapshot: { status: IaCreditsReservationStatus }
      },
  hasFinalResult: boolean
): IaCreditsRecoveryPolicy {
  if (lookup.kind === "unavailable") return { action: "poll", retry: "blocked" }
  if (lookup.kind === "not_found") return { action: "stop", retry: "same_operation" }
  if (lookup.kind === "unauthenticated" || lookup.kind === "forbidden") {
    return { action: "auth_error", retry: "blocked" }
  }
  if (lookup.kind === "invalid_request") {
    return { action: "invalid_request", retry: "blocked" }
  }

  switch (lookup.snapshot.status) {
    case "reserved":
      return { action: "poll", retry: "blocked" }
    case "released":
    case "expired":
      return { action: "stop", retry: "new_operation" }
    case "committed":
      return hasFinalResult
        ? { action: "use_final_result", retry: "blocked" }
        : { action: "committed_without_result", retry: "blocked" }
  }
}

function abortError() {
  return new DOMException("Polling cancelado.", "AbortError")
}

function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) throw abortError()
}

async function waitForPollingDelay(delayMs: number, signal?: AbortSignal) {
  throwIfAborted(signal)
  await new Promise<void>((resolve, reject) => {
    const timer: { id?: ReturnType<typeof setTimeout> } = {}
    const onAbort = () => {
      if (timer.id !== undefined) clearTimeout(timer.id)
      signal?.removeEventListener("abort", onAbort)
      reject(abortError())
    }
    timer.id = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort)
      resolve()
    }, delayMs)
    signal?.addEventListener("abort", onAbort, { once: true })
  })
  throwIfAborted(signal)
}

export async function pollIaCreditsReservationStatus({
  lookup,
  signal,
  wait = waitForPollingDelay,
  initialDelayMs = 800,
  maxDelayMs = 5_000,
  onLookup,
}: {
  lookup: (signal?: AbortSignal) => Promise<IaCreditsReservationLookup>
  signal?: AbortSignal
  wait?: (delayMs: number, signal?: AbortSignal) => Promise<void>
  initialDelayMs?: number
  maxDelayMs?: number
  onLookup?: (lookup: IaCreditsReservationLookup) => void
}): Promise<IaCreditsReservationLookup> {
  let delayMs = initialDelayMs
  while (true) {
    throwIfAborted(signal)
    await wait(delayMs, signal)
    throwIfAborted(signal)
    let result: IaCreditsReservationLookup
    try {
      result = await lookup(signal)
    } catch (error) {
      if (signal?.aborted || (error instanceof Error && error.name === "AbortError")) throw error
      result = { kind: "unavailable" }
    }
    throwIfAborted(signal)
    onLookup?.(result)
    if (getIaCreditsRecoveryPolicy(result, false).action !== "poll") return result
    delayMs = Math.min(maxDelayMs, Math.round(delayMs * 1.5))
  }
}

/**
 * Normaliza apenas os campos públicos usados pela UI. Mensagens e detalhes do
 * backend são deliberadamente descartados para não expor erros internos.
 */
export function parseIaCreditsGenerationError(
  status: number,
  payload: unknown
): IaCreditsGenerationError {
  const root = asRecord(payload)
  if (status === 402 && root?.error === "NO_IA_CREDITS") {
    const needed = asNonNegativeFiniteNumber(root.needed)
    const remaining = asNonNegativeFiniteNumber(root.remaining)
    const total = asNonNegativeFiniteNumber(root.total)
    if (needed !== null && remaining !== null) {
      return {
        code: "NO_IA_CREDITS",
        needed,
        remaining,
        ...(total === null ? {} : { total }),
      }
    }
  }
  if (status === 202 && root?.error === "OPERATION_IN_PROGRESS") {
    return { code: "OPERATION_IN_PROGRESS" }
  }

  switch (status) {
    case 401:
      return { code: "AUTHENTICATION_REQUIRED" }
    case 403:
      return { code: "AUTHORIZATION_FORBIDDEN" }
    case 409:
      return { code: "OPERATION_IN_PROGRESS" }
    case 413:
      return { code: "PAYLOAD_TOO_LARGE" }
    case 503:
      return { code: "SERVICE_UNAVAILABLE" }
    default:
      return { code: "GENERATION_FAILED" }
  }
}

type IaCreditsPayload = {
  success?: boolean
  error?: string
  remaining?: number | null
  total?: number | null
  isUnlimited?: boolean
  needed?: number
  spent?: number
  cost?: number
}

function unwrapClientPayload(resp: unknown): IaCreditsPayload | null {
  if (!resp || typeof resp !== "object") return null

  const root = resp as IaCreditsPayload

  if (root.success === true) return root

  if (root.error && typeof root.error === "object") {
    return root.error as IaCreditsPayload
  }

  return root
}

/**
 * Decide apenas quando o backend informou um entitlement ilimitado ou um saldo
 * finito. `null` significa resposta inconclusiva e deve deixar o backend decidir.
 */
export function canSpendIaCredits(resp: unknown, cost: number): boolean | null {
  const payload = unwrapClientPayload(resp)
  if (!payload) return null
  if (payload.isUnlimited === true) return true
  if (typeof payload.remaining !== "number" || !Number.isFinite(payload.remaining)) return null

  return payload.remaining >= cost
}

export type IaCreditsSpendResult =
  | {
      ok: true
      spent: number
      remaining: number
      cost: number
    }
  | {
      ok: false
      code: string
      remaining: number
      needed: number
    }

export function parseIaCreditsSpendResponse(resp: unknown): IaCreditsSpendResult {
  const payload = unwrapClientPayload(resp)

  if (!payload) {
    return { ok: false, code: "UNKNOWN", remaining: 0, needed: 0 }
  }

  if (payload.success === true) {
    return {
      ok: true,
      spent: Number(payload.spent ?? payload.cost ?? 0),
      remaining: Number(payload.remaining ?? 0),
      cost: Number(payload.cost ?? payload.spent ?? 0),
    }
  }

  return {
    ok: false,
    code: typeof payload.error === "string" ? payload.error : "UNKNOWN",
    remaining: Number(payload.remaining ?? 0),
    needed: Number(payload.needed ?? 0),
  }
}
